import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, KeyRound } from 'lucide-react';
import { authClient } from '@/lib/auth-client';
import { api } from '@/lib/api-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { Button } from '@/components/ui/button';
import { OtpInput, BackupCodeInput } from '@/components/ui/otp-input';

export const Route = createFileRoute('/verify-2fa')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: Verify2FAPage,
});

function Verify2FAPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [isBackupMode, setIsBackupMode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePostLogin() {
    try {
      const orgs = await authClient.organization.list();
      if (orgs.data && orgs.data.length > 0) {
        await authClient.organization.setActive({ organizationId: orgs.data[0].id });
      }
    } catch {
      // onboarding will handle
    }

    try {
      const onboarding = await api.get<{
        data: { profileCompleted: boolean; workspaceCompleted: boolean; deactivated?: boolean };
      }>('/users/me/onboarding');
      if (onboarding.data?.deactivated) {
        await navigate({ to: '/deactivated' });
        return;
      }
      if (!onboarding.data?.profileCompleted || !onboarding.data?.workspaceCompleted) {
        await navigate({ to: '/onboarding' });
        return;
      }
    } catch {
      // proceed to dashboard
    }

    await navigate({ to: '/dashboard' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim() || isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    try {
      if (isBackupMode) {
        const result = await authClient.twoFactor.verifyBackupCode({
          code: code.trim(),
          trustDevice,
        });
        if (result.error) {
          setError(t('profile.verify2FA.invalidCode'));
          return;
        }
      } else {
        const result = await authClient.twoFactor.verifyTotp({
          code: code.trim(),
          trustDevice,
        });
        if (result.error) {
          setError(t('profile.verify2FA.invalidCode'));
          return;
        }
      }

      await handlePostLogin();
    } catch {
      setError(t('profile.verify2FA.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  function toggleMode() {
    setIsBackupMode((prev) => !prev);
    setCode('');
    setError(null);
  }

  const canSubmit = isBackupMode ? code.length >= 8 : code.length === 6;

  return (
    <AuthLayout
      title={t('profile.verify2FA.title')}
      description={
        isBackupMode
          ? t('profile.verify2FA.backupCodeDescription')
          : t('profile.verify2FA.description')
      }
    >
      <div className="flex justify-center mb-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
          <ShieldCheck className="size-8 text-primary" />
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-5">
          {isBackupMode ? (
            <BackupCodeInput
              value={code}
              onChange={setCode}
              disabled={isSubmitting}
              autoFocus
              placeholder="aBcD0-eFgH1"
            />
          ) : (
            <OtpInput value={code} onChange={setCode} disabled={isSubmitting} autoFocus />
          )}

          {error && (
            <div
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3"
              role="alert"
            >
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              disabled={isSubmitting}
              className="size-4 cursor-pointer rounded border-input accent-primary"
            />
            <span className="text-sm text-muted-foreground">
              {t('profile.verify2FA.trustDevice')}
            </span>
          </label>

          <Button
            type="submit"
            className="h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
            size="lg"
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('profile.verify2FA.verifying')}
              </>
            ) : (
              t('profile.verify2FA.verify')
            )}
          </Button>

          <button
            type="button"
            onClick={toggleMode}
            className="flex w-full cursor-pointer items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <KeyRound className="size-3.5" />
            {isBackupMode
              ? t('profile.verify2FA.useAuthenticator')
              : t('profile.verify2FA.useBackupCode')}
          </button>
        </div>
      </form>
    </AuthLayout>
  );
}
